// @flow
import React from "react";
import radium, { Style } from "radium";
import { connect } from "react-redux";
import shallowCompare from "react-addons-shallow-compare";
import { color, media, zIndex } from "rizzo-next/sass/settings.json";
import Place from "../place";
import ExpandButton from "../expandButton";
import Bookmark from "../bookmark";
import { gutter } from "../../utils/grid";
import { rgb } from "../../utils/color";
import { createDetailUrlFromResource } from "../../../../../server/utils/createUrls";
import actions from "../../actions";
import { distanceTo, distanceUnits } from "../../utils/distanceTo";
import resourceTypes from "../../../../../server/lib/open-planet/resourceTypes";
import type {
  Breadcrumb,
  Poi,
} from "../../../../../server/types";

let InteractiveMap;

if (typeof window !== "undefined") {
  /* eslint-disable */
  InteractiveMap = require("../interactiveMap").connected;
  /* eslint-enable */
}

const mapHeight = "380px";

const placeHover = {
  backgroundColor: `rgba(${rgb(color.gray)}, .3)`,
};

const styles = {
  container: {
    base: {
      [`@media (min-width: ${media.min["1080"]})`]: {
        height: mapHeight,
        maxHeight: mapHeight,
      },
    },
  },

  column: {
    base: {
      [`@media (max-width: ${media.max["768"]}) and (orientation: landscape)`]: {
        float: "left",
        height: "100vh",
        width: "50vw",
      },

      [`@media (min-width: ${media.min["1080"]})`]: {
        float: "left",
        height: "100%",
        width: "328px",
      },
    },
  },

  map: {
    container: {
      backgroundColor: `rgba(${rgb(color.lightSlate)}, .5)`,
      maxHeight: mapHeight,
      overflow: "hidden",
      position: "relative",

      [`@media (min-width: ${media.min["1080"]})`]: {
        width: "calc(100% - 328px)",
      },

      [`@media (max-width: ${media.max["1080"]}) and (orientation: portrait)`]: {
        height: "31vh",
      },
    },
  },

  expandButton: {
    base: {
      bottom: "2.4rem",
      position: "absolute",
      right: ".8rem",
      zIndex: zIndex.top,
    },
  },

  list: {
    base: {
      [`@media (max-width: ${media.max["1080"]}) and (orientation: portrait)`]: {
        borderBottom: `.1rem solid ${color.gray}`,
      },
    },
  },

  place: {
    base: {
      cursor: "pointer",
      position: "relative",

      [`@media (max-width: ${media.max["768"]}) and (orientation: landscape)`]: {
        height: "25vh",
        paddingLeft: gutter("static", 12, 0.5),
        paddingRight: gutter("static", 12, 0.5),
      },

      [`@media (max-width: ${media.max["1080"]})`]: {
        display: "flex",
        alignItems: "center",
        padding: "2rem",

        ":hover": placeHover,
        ":active": placeHover,
        ":focus": placeHover,
      },

      [`@media (min-width: ${media.min["1080"]})`]: {
        marginLeft: gutter("static"),
        paddingBottom: "2rem",
        paddingTop: "2rem",
      },
    },

    notFirst: {
      borderBottom: `.1rem solid rgba(${rgb(color.gray)}, .3)`,
    },

    first: {
      borderTop: `.1rem solid rgba(${rgb(color.gray)}, .3)`,
      borderBottom: `.1rem solid rgba(${rgb(color.gray)}, .3)`,

      [`@media (min-width: ${media.min["1080"]})`]: {
        paddingTop: "2.1rem",
      },
    },

    background: {
      base: {
        [`@media (max-width: ${media.max["1080"]})`]: {
          display: "none",
        },

        [`@media (min-width: ${media.min["1080"]})`]: {
          height: "calc(100% + .1rem)",
          left: `-${gutter("static")}`,
          position: "absolute",
          top: 0,
          width: `calc(100% + ${gutter("static")} + ${gutter("static")})`,
          zIndex: zIndex.below,
        },
      },

      hover: placeHover,

      last: {
        height: "100%",
      },
    },

    bookmark: {
      height: "100%",
      position: "absolute",
      top: 0,
      right: 0,

      [`@media (max-width: ${media.max["1080"]})`]: {
        right: "1.6rem",
      },
    },
  },
};

export type NearbyPlacesProps = {
  places: Array<Poi>,
  currentPlace: Poi,
  mapId: number,
  expandable: boolean,
  showBookmark: boolean,
  breadcrumb: Array<Breadcrumb>,
  activePlace: Poi,
  dispatch: Function,
  relatedPlaceId: number,
}

/**
 * Nearby places map
 */
export default class NearbyPlaces extends React.Component {
  static styles = styles;

  constructor() {
    super();

    this.state = {
      renderMap: typeof window !== "undefined",
    };

    this.setActivePoi = this.setActivePoi.bind(this);
    this.clearActivePoi = this.clearActivePoi.bind(this);
    this.distanceToNearby = this.distanceToNearby.bind(this);
  }

  state: {
    renderMap: boolean,
  };

  componentDidMount() {
    const { places, currentPlace /* links, place, */ } = this.props;

    const resourceId = (currentPlace.type === resourceTypes.lodging && currentPlace.poi) ?
      currentPlace.poi.id :
      currentPlace.id;

    if (
      (currentPlace.type === resourceTypes.lodging || currentPlace.type === resourceTypes.poi) &&
      resourceId && !places.length) {
      this.props.dispatch(actions.fetchNearby(resourceId));
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  setActivePoi(event, place) {
    if (typeof event.target.setOpacity === "function") {
      event.target.setOpacity(1);
    }

    this.props.dispatch(actions.hoverPlace(place));
  }

  setActivePoi: () => void;
  clearActivePoi: () => void;
  distanceToNearby: () => void;
  props: NearbyPlacesProps;

  clearActivePoi(event) {
    if (typeof event.target.setOpacity === "function") {
      event.target.setOpacity(0.4);
    }

    this.props.dispatch(actions.hoverPlace({}));
  }

  distanceToNearby(place) {
    const distance = distanceTo(
      place.location.coordinates,
      this.props.currentPlace.location.coordinates
    );

    return `${distance[distanceUnits()].amount} ${distance[distanceUnits()].unit}`;
  }

  render() {
    const {
      places,
      currentPlace,
      mapId,
      expandable,
      showBookmark,
      breadcrumb,
      activePlace,
      relatedPlaceId,
    } = this.props;

    const PlacesList = places.filter((p) => p.location).map((place, index) => (
      <div
        className="NearbyPlaces-place"
        id={`nearby-poi-${place.id}`}
        style={[
          styles.place.base,
          styles.place.notFirst,
          index === 0 && styles.place.first,
        ]}
        key={index}
        onMouseEnter={(event) => this.setActivePoi(event, place)}
        onMouseLeave={this.clearActivePoi}
      >
        <Place
          key={`place-${index}`}
          type="nearby"
          title={place.name}
          slug={createDetailUrlFromResource(
            place,
            breadcrumb.map(b => b.title),
            relatedPlaceId
          )}
          category={place.poiType}
          image={(place.imageAssociations && place.imageAssociations.from) ?
            `//lonelyplanetimages.imgix.net${place.imageAssociations.from.path}?auto=enhance&w=80&h=54&fit=crop` : // eslint-disable-line max-len
            ""}
          coords={place.location.coordinates}
          distanceToNearby={this.distanceToNearby(place)}
        />

        {showBookmark &&
          <div className="NearbyPlaces-placeBookmark" style={styles.place.bookmark}>
            <Bookmark
              size="small"
              center="vertical"
              align="right"
              onClick={null}
            />
          </div>
        }

        <div
          className="NearbyPlaces-placeBackground"
          style={[
            styles.place.background.base,
            parseInt(activePlace.id, 10) === parseInt(place.id, 10)
              && styles.place.background.hover,
            index === places.length - 1 && styles.place.background.last,
          ]}
          key={`background-${index}`}
        />
      </div>
    ));

    return (
      <div className="NearbyPlaces clearfix" style={styles.container.base}>
        <Style
          scopeSelector=".NearbyPlaces"
          rules={{
            ".Place-content": {
              width: "calc(100% - 150px)",
            },
          }}
        />

        <div
          className="NearbyPlaces-mapContainer"
          style={[
            styles.column.base,
            styles.map.container,
          ]}
        >
          {this.state.renderMap && currentPlace.location && places.length > 0 &&
            <InteractiveMap
              center={currentPlace.location.coordinates.slice(0).reverse()}
              centerName={currentPlace.name}
              places={places}
              mapId={mapId}
              markerOpacity={0.4}
              mode="hover"
              fill
              hideZoomControls
              fitBounds
            />
          }

          {expandable &&
            <div
              className="NearbyPlaces-expandButton"
              style={styles.expandButton.base}
            >
              <ExpandButton
                onClick={null}
              />
            </div>
          }
        </div>

        <div
          className="NearbyPlaces-list"
          style={[
            styles.column.base,
            styles.list.base,
          ]}
        >
          {PlacesList}
        </div>
      </div>
    );
  }
}

NearbyPlaces.propTypes = {
  /**
   * Array of nearby POIs to plot on the map
   */
  places: React.PropTypes.array.isRequired,

  /**
   * Data for the current place
   */
  currentPlace: React.PropTypes.object,

  /**
   * Data for a related place that is active, meaning it has been hovered; data
   * comes from mapStateToProps
   */
  activePlace: React.PropTypes.object,

  /**
   * ID for the InteractiveMap
   */
  mapId: React.PropTypes.string,

  /**
   * Whether or not the map is expandable into full-screen mode
   */
  expandable: React.PropTypes.bool,

  /**
   * Whether or not the bookmark button should be shown for each item
   */
  showBookmark: React.PropTypes.bool,

  /**
   * Store dispatch method
   */
  dispatch: React.PropTypes.func,

  /**
   * Array of breadcrumbs
   */
  breadcrumb: React.PropTypes.array,
};

NearbyPlaces.defaultProps = {
  currentPlace: {},

  activePlace: {},

  places: [],

  mapId: "",

  expandable: false,

  slug: "",

  showBookmark: false,
};

const mapStateToProps = (state, props) => ({
  activePlace: state.nearbyPois.active || {},
  breadcrumb: state.breadcrumb,
  places: state.nearbyPois.pois[props.currentPlace.id] || [],
});

const connected = connect(mapStateToProps)(radium(NearbyPlaces));
export { connected };



// WEBPACK FOOTER //
// ./assets/pois/components/nearbyPlaces/index.jsx